/**
 * Displays text and screenshot references captured from the clinical PDF.
 * @module medicalNecessityAnalysis/components/PdfProofList
 */
import React from "react";
import AlertCircleIcon from "../../../svg/alert-circle-icon";
import { EditSquareIcon } from "../../../svg/editSquareIcon";
import { TrashIcon } from "../../../svg/trashIcon";
import { PdfProof } from "../types/pdfProof";
import { isGenericComment } from "../utils/genericCommentValidation";

/** Props for {@link PdfProofList}. */
interface PdfProofListProps {
  /** Confirmed PDF references to render. */
  proofs: PdfProof[];
  /** Reopens a reference in the review dialog. */
  onEdit: (proofId: string) => void;
  /** Drops a reference from the active session. */
  onRemove: (proofId: string) => void;
}

/**
 * Renders compact cards for each confirmed text or screenshot reference.
 *
 * @param props - PDF references with their edit and removal callbacks
 * @returns List of selected text excerpts and screenshot labels
 */
export const PdfProofList: React.FC<PdfProofListProps> = ({
  proofs,
  onEdit,
  onRemove,
}) => {
  const annotationProofs = proofs.filter((proof) => proof.type === "pdf_text");
  const screenshotProofs = proofs.filter(
    (proof) => proof.type === "pdf_screenshot",
  );

  return (
    <div className="grid grid-cols-2 gap-2">
      {[annotationProofs, screenshotProofs].map((group, groupIndex) =>
        group.length > 0 ? (
          <div key={groupIndex} className="flex min-w-0 flex-col gap-2">
            <span className="text-[10px] font-semibold text-[#0F0F0F]">
              {groupIndex === 0 ? "Annotation" : "Screenshot"}
            </span>
            {group.map((proof, proofIndex) => (
              <div
                key={proof.id}
                className="flex h-9 min-w-0 items-center gap-2 rounded-sm bg-[#F5F5F5] px-3"
              >
                {isGenericComment(proof.comment ?? "") && (
                  <span
                    className="shrink-0"
                    aria-label="Comment may be too generic"
                  >
                    <AlertCircleIcon width="14" height="14" stroke="#C24400" />
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-[10px] text-[#374151]">
                  {proof.type === "pdf_text"
                    ? proof.text
                    : `Screenshot ${proofIndex + 1}`}
                </span>
                <button
                  type="button"
                  aria-label={`Edit ${
                    proof.type === "pdf_text" ? "annotation" : "screenshot"
                  }`}
                  onClick={() => onEdit(proof.id)}
                  className="shrink-0 text-[#006BC9]"
                >
                  <EditSquareIcon width={16} height={16} />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${
                    proof.type === "pdf_text" ? "annotation" : "screenshot"
                  }`}
                  onClick={() => onRemove(proof.id)}
                  className="shrink-0"
                >
                  <TrashIcon width={16} height={16} />
                </button>
              </div>
            ))}
          </div>
        ) : null,
      )}
    </div>
  );
};
